import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { apiService } from '../services/api';
import * as fmt from '../lib/format';

interface TagRow {
  group: string;
  log_type: string;
  hours: number;
}

interface Props {
  month: string; // "YYYY-MM"
}


export default function TagDistributionView({ month }: Props) {
  const [rows, setRows] = useState<TagRow[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0); // bumped by Try again
  const [selectedGroup, setSelectedGroup] = useState<string>('All');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiService
      .getWorklogTagDistribution(month)
      .then((res: any) => {
        if (cancelled) return;
        if (res.data?.success) {
          setRows(res.data.data || []);
          setGroups(res.data.groups || []);
        } else {
          setRows([]);
          setGroups([]);
          setError(res.data?.error || 'Could not load the time distribution');
        }
      })
      .catch((e: any) => {
        if (cancelled) return;
        setRows([]);
        setGroups([]);
        setError(e?.response?.data?.error || e?.message || 'Could not load the time distribution');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [month, attempt]);

  // Aggregate the selected group's hours by tag (exclude the untagged "[]" bucket from the chart).
  const byTag = useMemo(() => {
    const scoped = selectedGroup === 'All' ? rows : rows.filter((r) => r.group === selectedGroup);
    const map = new Map<string, number>();
    for (const r of scoped) {
      if (r.log_type === '[]') continue;
      map.set(r.log_type, (map.get(r.log_type) || 0) + r.hours);
    }
    return Array.from(map.entries())
      .map(([name, hours]) => ({ name, hours }))
      .sort((a, b) => b.hours - a.hours);
  }, [rows, selectedGroup]);

  const total = useMemo(() => byTag.reduce((s, t) => s + t.hours, 0), [byTag]);
  const top = byTag[0];
  const share = top && total > 0 ? Math.round((top.hours / total) * 100) : 0;

  const pills = ['All', ...groups];
  const chartHeight = Math.max(220, byTag.length * 44);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Time distribution</h3>
        <p className="text-sm text-gray-500">Where the team's logged hours are going</p>
      </div>

      {/* Group filter pills */}
      <div className="flex flex-wrap items-center gap-2">
        {pills.map((g) => {
          const active = selectedGroup === g;
          return (
            <button
              key={g}
              onClick={() => setSelectedGroup(g)}
              className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                active ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {g}
            </button>
          );
        })}
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Total logged</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{fmt.hours(total)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Top category</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{top ? top.name : fmt.MISSING}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Share on {top ? top.name : fmt.MISSING}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{fmt.percent(share)}</p>
        </div>
      </div>

      {/* Bar chart */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4">
        {loading ? (
          <div className="flex h-64 items-center justify-center text-sm text-gray-500">Loading…</div>
        ) : error ? (
          <div role="alert" className="flex h-64 flex-col items-center justify-center gap-3 text-sm text-rose-700">
            <span>The time distribution could not be loaded: {error}</span>
            <button
              type="button"
              onClick={() => setAttempt((a) => a + 1)}
              className="rounded-lg bg-white px-3 py-1 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition-colors hover:bg-rose-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
            >
              Try again
            </button>
          </div>
        ) : byTag.length === 0 ? (
          <div className="flex h-64 items-center justify-center text-sm text-gray-500">
            No tagged worklogs for this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart data={byTag} layout="vertical" margin={{ top: 4, right: 48, bottom: 4, left: 8 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e5e7eb' }} />
              <YAxis
                type="category"
                dataKey="name"
                width={110}
                tick={{ fontSize: 12, fill: '#334155' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                formatter={(v: any) => [fmt.hours(Number(v)), 'Hours']}
                cursor={{ fill: 'rgba(37,99,235,0.06)' }}
                contentStyle={{ borderRadius: 12, border: '1px solid #e5e7eb', fontSize: 12 }}
              />
              <Bar dataKey="hours" fill="#1e6fe0" radius={[0, 4, 4, 0]} barSize={16}>
                <LabelList
                  dataKey="hours"
                  position="right"
                  formatter={(v: any) => fmt.hours(Number(v))}
                  style={{ fontSize: 11, fill: '#334155', fontWeight: 600 }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
